import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface DocumentUpload {
  id: string
  fileName: string
  fileSize: number
  mimeType: string
  category: 'income' | 'assets' | 'collateral' | 'capacity' | 'documents' | 'general'
  subcategory?: string // e.g., 'w2', 'paystub', 'bank_statement'
  caseId: string
  uploadedAt: Date
  status: 'uploading' | 'processing' | 'extracting' | 'complete' | 'error'
  progress: number // 0-100
  extractedData?: any
  error?: string
}

export interface ProcessingEvent {
  documentId: string
  type: 'upload_started' | 'upload_progress' | 'upload_complete' | 'extraction_started' | 'extraction_complete' | 'error'
  timestamp: Date
  data?: any
}

interface DocumentProcessingState {
  uploads: Record<string, DocumentUpload>
  activeUploads: string[]
  processingQueue: string[]
  events: ProcessingEvent[]
}

const initialState: DocumentProcessingState = {
  uploads: {},
  activeUploads: [],
  processingQueue: [],
  events: []
}

const documentProcessingSlice = createSlice({
  name: 'documentProcessing',
  initialState,
  reducers: {
    startUpload: (state, action: PayloadAction<{
      id: string
      fileName: string
      fileSize: number
      mimeType: string
      category: DocumentUpload['category']
      subcategory?: string
      caseId: string
    }>) => {
      const { id, fileName, fileSize, mimeType, category, subcategory, caseId } = action.payload
      state.uploads[id] = {
        id,
        fileName,
        fileSize,
        mimeType,
        category,
        subcategory,
        caseId,
        uploadedAt: new Date(),
        status: 'uploading',
        progress: 0
      }
      state.activeUploads.push(id)
      state.events.push({
        documentId: id,
        type: 'upload_started',
        timestamp: new Date(),
        data: { fileName, category }
      })
    },

    updateUploadProgress: (state, action: PayloadAction<{ id: string; progress: number }>) => {
      const { id, progress } = action.payload
      if (state.uploads[id]) {
        state.uploads[id].progress = progress
        state.events.push({
          documentId: id,
          type: 'upload_progress',
          timestamp: new Date(),
          data: { progress }
        })
      }
    },

    completeUpload: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      if (state.uploads[id]) {
        state.uploads[id].status = 'processing'
        state.uploads[id].progress = 100
        state.activeUploads = state.activeUploads.filter(uploadId => uploadId !== id)
        state.processingQueue.push(id)
        state.events.push({
          documentId: id,
          type: 'upload_complete',
          timestamp: new Date()
        })
      }
    },

    startExtraction: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      if (state.uploads[id]) {
        state.uploads[id].status = 'extracting'
        state.events.push({
          documentId: id,
          type: 'extraction_started',
          timestamp: new Date()
        })
      }
    },

    completeExtraction: (state, action: PayloadAction<{ id: string; extractedData: any }>) => {
      const { id, extractedData } = action.payload
      if (state.uploads[id]) {
        state.uploads[id].status = 'complete'
        state.uploads[id].extractedData = extractedData
        state.processingQueue = state.processingQueue.filter(docId => docId !== id)
        state.events.push({
          documentId: id,
          type: 'extraction_complete',
          timestamp: new Date(),
          data: extractedData
        })
        
        // Update the sidebar to show the new document
        // This will be handled by a middleware or saga
      }
    },

    setUploadError: (state, action: PayloadAction<{ id: string; error: string }>) => {
      const { id, error } = action.payload
      if (state.uploads[id]) {
        state.uploads[id].status = 'error'
        state.uploads[id].error = error
        state.activeUploads = state.activeUploads.filter(uploadId => uploadId !== id)
        state.processingQueue = state.processingQueue.filter(docId => docId !== id)
        state.events.push({
          documentId: id,
          type: 'error',
          timestamp: new Date(),
          data: { error }
        })
      }
    },

    clearUpload: (state, action: PayloadAction<string>) => {
      const id = action.payload
      delete state.uploads[id]
      state.activeUploads = state.activeUploads.filter(uploadId => uploadId !== id)
      state.processingQueue = state.processingQueue.filter(docId => docId !== id)
    },

    clearAllUploads: (state) => {
      state.uploads = {}
      state.activeUploads = []
      state.processingQueue = []
      state.events = []
    }
  }
})

export const {
  startUpload,
  updateUploadProgress,
  completeUpload,
  startExtraction,
  completeExtraction,
  setUploadError,
  clearUpload,
  clearAllUploads
} = documentProcessingSlice.actions

export default documentProcessingSlice.reducer

// Selectors
export const selectUploads = (state: { documentProcessing: DocumentProcessingState }) => state.documentProcessing.uploads
export const selectActiveUploads = (state: { documentProcessing: DocumentProcessingState }) => state.documentProcessing.activeUploads
export const selectProcessingQueue = (state: { documentProcessing: DocumentProcessingState }) => state.documentProcessing.processingQueue
export const selectProcessingEvents = (state: { documentProcessing: DocumentProcessingState }) => state.documentProcessing.events
export const selectUploadsByCategory = (category: DocumentUpload['category']) => (state: { documentProcessing: DocumentProcessingState }) => {
  return Object.values(state.documentProcessing.uploads).filter(upload => upload.category === category)
}
export const selectUploadsByCaseId = (caseId: string) => (state: { documentProcessing: DocumentProcessingState }) => {
  return Object.values(state.documentProcessing.uploads).filter(upload => upload.caseId === caseId)
}
